import React, { useLayoutEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Ban,
  Check,
  ChevronRight,
  Fingerprint,
  KeyRound,
  LockKeyhole,
  ScanLine,
  ShieldCheck,
  TimerReset,
} from 'lucide-react';
import Header from './Header';
import Footer from './Footer';
import '../styles/software-protection.css';

const passportFields = [
  ['user', 'verified'],
  ['machine', 'bound'],
  ['session', 'single run'],
  ['release', 'hardened'],
  ['state', 'valid now'],
];

const comparisonRows = [
  {
    topic: 'Unlock model',
    basic: 'A copied key can keep unlocking the app until someone notices.',
    protected: 'Every run asks for a live, user-bound session before the app is trusted.',
  },
  {
    topic: 'Machine binding',
    basic: 'HWID becomes the whole defense, even when the account or build is abused.',
    protected: 'Machine context is checked alongside identity, entitlement, and run state.',
  },
  {
    topic: 'Leaked builds',
    basic: 'The same leaked build stays valuable because it can still pass local checks.',
    protected: 'Reused artifacts are designed to lose value outside the approved context.',
  },
  {
    topic: 'Patch surface',
    basic: 'Attackers get obvious client-side checks to find, patch, and share.',
    protected: 'Sensitive paths can be hardened, hidden, obfuscated, and session-aware.',
  },
];

const layers = [
  {
    icon: Fingerprint,
    title: 'Identity',
    text: 'Logged-in user, entitlement, account state, and product access are checked before the run is trusted.',
  },
  {
    icon: ScanLine,
    title: 'Machine',
    text: 'The session is evaluated against the environment it was meant to run on, not only a copied key.',
  },
  {
    icon: TimerReset,
    title: 'Session',
    text: 'Validity is scoped to a live run, so a leaked artifact is not treated as a permanent asset.',
  },
  {
    icon: LockKeyhole,
    title: 'Binary',
    text: 'Protected imports, release hardening, and optional obfuscation raise the cost of patching the client.',
  },
];

const audience = [
  'Paid Windows software with real license abuse',
  'SaaS companion clients that should only run for active users',
  'Private loaders, launchers, and commercial automation tools',
  'Teams that have outgrown key-only authentication dashboards',
];

const auditSteps = [
  'Map your current auth and release flow',
  'Identify reusable binary and patch targets',
  'Design the protected-session boundary',
  'Integrate protection without wrecking developer workflow',
];

const SoftwareProtectionPage = ({ isMenuOpen, setIsMenuOpen }) => {
  useLayoutEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, []);

  return (
    <div className="software-protection-page min-h-screen text-white overflow-x-hidden">
      <Helmet>
        <title>Software Protection | TukeWIN</title>
        <link
          rel="stylesheet"
          href="https://fonts.googleapis.com/css2?family=Unbounded:wght@800;900&display=swap"
        />
        <meta
          name="description"
          content="Software protection for paid applications that need more than license keys: protected sessions, machine-aware validation, release hardening, and optional code obfuscation."
        />
        <meta
          name="keywords"
          content="software protection, protected sessions, application licensing, anti crack, code obfuscation, SaaS software protection"
        />
      </Helmet>

      <Header isMenuOpen={isMenuOpen} setIsMenuOpen={setIsMenuOpen} />

      <main>
        <section className="sp-hero">
          <div className="sp-hero-field" aria-hidden="true" />
          <div className="sp-container sp-hero-layout">
            <div className="sp-hero-copy">
              <p className="sp-label">Software Protection</p>
              <h1>Not another license key dashboard.</h1>
              <p className="sp-hero-text">
                Protected sessions for paid software: user-bound, machine-aware, hardened per run.
                Built for products where copied binaries, shared accounts, and patched checks are
                not theoretical problems.
              </p>
              <div className="sp-hero-actions">
                <Link to="/contact" className="sp-button sp-button-primary">
                  Request protection audit
                  <ArrowRight className="h-4 w-4" />
                </Link>
                <a href="#difference" className="sp-button sp-button-secondary">
                  See what changes
                </a>
              </div>
            </div>

            <aside className="sp-passport" aria-label="Protected session permit">
              <div className="sp-passport-top">
                <div>
                  <span>Execution permit</span>
                  <strong>valid run only</strong>
                </div>
                <ShieldCheck className="h-7 w-7" />
              </div>
              <div className="sp-passport-stamp">SESSION APPROVED</div>
              <dl className="sp-passport-fields">
                {passportFields.map(([label, value]) => (
                  <div key={label}>
                    <dt>{label}</dt>
                    <dd>{value}</dd>
                  </div>
                ))}
              </dl>
              <div className="sp-passport-footer">
                <span>artifact value</span>
                <strong>expires outside context</strong>
              </div>
            </aside>
          </div>
        </section>

        <section id="difference" className="sp-section sp-compare-section">
          <div className="sp-container">
            <div className="sp-section-heading">
              <p className="sp-label">Where ordinary licensing stops</p>
              <h2>Key checks are useful. They are not the product defense.</h2>
            </div>

            <div
              className="sp-comparison"
              aria-label="Typical auth licensing compared with TukeWIN protected sessions"
            >
              <div className="sp-comparison-head sp-comparison-topic-head">
                Decision point
              </div>
              <div className="sp-comparison-head basic">
                <KeyRound className="h-5 w-5" />
                <span>Ordinary licensing</span>
              </div>
              <div className="sp-comparison-head protected">
                <ShieldCheck className="h-5 w-5" />
                <span>Protected session</span>
              </div>
              {comparisonRows.map((row) => (
                <React.Fragment key={row.topic}>
                  <div className="sp-comparison-topic-cell">
                    <span>{row.topic}</span>
                  </div>
                  <div className="sp-comparison-cell basic">
                    <span className="sp-mobile-column-label">Ordinary licensing</span>
                    <Ban className="h-4 w-4" />
                    <p>{row.basic}</p>
                  </div>
                  <div className="sp-comparison-cell protected">
                    <span className="sp-mobile-column-label">Protected session</span>
                    <Check className="h-4 w-4" />
                    <p>{row.protected}</p>
                  </div>
                </React.Fragment>
              ))}
            </div>
          </div>
        </section>

        <section className="sp-section sp-manifesto">
          <div className="sp-container sp-manifesto-layout">
            <p className="sp-label">Core principle</p>
            <h2>The binary should not be valuable forever.</h2>
            <p>
              A cracked or leaked build is dangerous because it keeps working. The protection service
              changes that equation: validity is tied to the active user, the expected machine context,
              the current session, and the hardened release artifact.
            </p>
            <p>
              The goal is not a marketing claim that nothing can be broken. The goal is sharper:
              make abuse expensive, short-lived, and visible enough to respond.
            </p>
          </div>
        </section>

        <section className="sp-section">
          <div className="sp-container sp-audience-layout">
            <div className="sp-section-heading">
              <p className="sp-label">Built for software people</p>
              <h2>Not checkout widgets. Not license spreadsheets.</h2>
              <p>
                This is for teams shipping real client software where the application itself needs
                protection, not only a payment record in a dashboard.
              </p>
            </div>
            <ul className="sp-audience-list">
              {audience.map((item) => (
                <li key={item}>
                  <ChevronRight className="h-4 w-4" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="sp-section sp-layers-section">
          <div className="sp-container">
            <div className="sp-section-heading narrow">
              <p className="sp-label">Protection layers</p>
              <h2>A run is trusted only when the layers agree.</h2>
            </div>
            <div className="sp-layer-rail">
              {layers.map((layer, index) => (
                <article key={layer.title} className="sp-layer">
                  <span className="sp-layer-index">{String(index + 1).padStart(2, '0')}</span>
                  <layer.icon className="h-6 w-6" />
                  <h3>{layer.title}</h3>
                  <p>{layer.text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="sp-section sp-obfuscation">
          <div className="sp-container sp-obfuscation-layout">
            <div className="sp-obfuscation-panel">
              <div className="sp-obfuscation-row">
                <span>imports</span>
                <strong>protected</strong>
              </div>
              <div className="sp-obfuscation-row">
                <span>control flow</span>
                <strong>hardened</strong>
              </div>
              <div className="sp-obfuscation-row">
                <span>sensitive code</span>
                <strong>obfuscated</strong>
              </div>
              <div className="sp-obfuscation-row">
                <span>release profile</span>
                <strong>per product</strong>
              </div>
            </div>
            <div>
              <p className="sp-label">Optional code obfuscator</p>
              <h2>Hardening where attackers would actually look.</h2>
              <p>
                Obfuscation is useful when it protects meaningful logic. We can deploy it as part of
                the protection design, focused on routines and imports that would otherwise shorten
                the path to a patch.
              </p>
            </div>
          </div>
        </section>

        <section className="sp-section sp-audit">
          <div className="sp-container sp-audit-layout">
            <div>
              <p className="sp-label">Audit first</p>
              <h2>Show us the release flow. We show you where it leaks value.</h2>
              <p>
                The first step is technical scoping. We review how users authenticate, how builds are
                distributed, where checks live, and how abuse currently survives.
              </p>
              <Link to="/contact" className="sp-button sp-button-primary">
                Request protection audit
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
            <ol className="sp-audit-steps">
              {auditSteps.map((step, index) => (
                <li key={step}>
                  <span>{String(index + 1).padStart(2, '0')}</span>
                  {step}
                </li>
              ))}
            </ol>
          </div>
        </section>

        <section className="sp-final">
          <div className="sp-container sp-final-inner">
            <h2>Sell the software. Protect the run.</h2>
            <Link to="/contact" className="sp-button sp-button-primary">
              Start with an audit
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default SoftwareProtectionPage;
